// src/pages/StoreCreditSettings.tsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Divider,
  useTheme,
  Grid,
  Paper,
  Alert,
  Button
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SettingsHeader from '../components/SettingsHeader';

const StoreCreditSettings: React.FC = () => {
  const theme = useTheme();
  const [isStoreCreditEnabled, setIsStoreCreditEnabled] = useState(false);

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsStoreCreditEnabled(event.target.checked);
    // Here you would typically make an API call to save the setting
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <SettingsHeader
        title="Store Credit"
        subtitle="Manage whether your stores offer store credit"
        icon={<CreditCardIcon fontSize="large" />}
      />

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              boxShadow: theme.shadows[4],
              borderRadius: 2,
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: theme.shadows[8]
              }
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Store Credit Settings
              </Typography>
              <Divider sx={{ my: 2 }} />

              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 3,
                  borderRadius: 2,
                  bgcolor: theme.palette.mode === 'dark' ? 'background.default' : 'grey.50'
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={isStoreCreditEnabled}
                      onChange={handleToggleChange}
                      color="primary"
                      size="medium"
                    />
                  }
                  label={
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Enable store credit in my store
                    </Typography>
                  }
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
                  Disabling store credit will mean you can no longer issue store credit or pay by store credit.
                </Typography>
              </Paper>

              {isStoreCreditEnabled && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  Store credit is currently enabled for your store. Customers can now use store credit for purchases.
                </Alert>
              )}

              {!isStoreCreditEnabled && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  Store credit is currently disabled. Customers cannot use store credit until you enable this feature.
                </Alert>
              )}

              <Divider sx={{ my: 3 }} />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button variant="outlined" color="inherit">
                  Cancel
                </Button>
                <Button variant="contained" color="primary">
                  Save Changes
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card
            sx={{
              boxShadow: theme.shadows[4],
              borderRadius: 2,
              height: '100%',
              bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'primary.light',
              color: theme.palette.mode === 'dark' ? 'text.primary' : 'primary.contrastText'
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <CreditCardIcon sx={{ mr: 1 }} /> About Store Credit
              </Typography>
              <Divider sx={{ my: 2, bgcolor: 'rgba(255,255,255,0.2)' }} />
              <Typography variant="body2" paragraph>
                Store credit allows our customers to hold a balance with your store that they can use for future purchases.
              </Typography>
              <Typography variant="body2" paragraph>
                When enabled, we can issue store credit to customers for returns, refunds, or as gifts.
              </Typography>
              <Typography variant="body2">
                Customers can view their store credit balance and use it at checkout.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StoreCreditSettings;